import { useContext, useMemo, useRef, useState } from 'react';

import ActivityTag from './components/activityTag';
import MultiLineText from './components/multiLineText';
import PicturePerview from './components/picturePerview';
import PriceLevelAndDiscount from './components/priceLevelAndDiscount';
import ProductTags from './components/productTags';
import PurchaseButton from './components/purchaseButton';
import PurchaseNumInputs from './components/purchaseNumInputs';
import StockAndDeliver from './components/stockAndDeliver';
import TotalPrice from './components/totalPrice';

import LabelText from '@lcsc-ui/components/LabelText';
import { LayoutContext } from '@lcsc-ui/layouts/baseLayout/provider';
import { ProductItemContext } from '@lcsc-ui/providers/productItem';
import { ProductListContext } from '@lcsc-ui/providers/productList';
import { buriedPoint, goItemBuriedPoint } from '@lcsc-ui/utils/buriedPoint';
import Utils from '@lcsc-ui/utils/Utils';

function ProductSpuItem(props) {
  const { resourceData, onlySScreen } = useContext(LayoutContext);
  const { track_zone, searchResult } = useContext(ProductListContext);
  const { index, rs_log } = useContext(ProductItemContext);

  const {
    productVO,
    paramLinkedMap, // 动态参数
    isMro, // 是否为mro
    remarkPrefixTitle,
    totalStockNumber,
    sideTitle,
    showBorder,
    suggest,
    needBold,
    showTag = true,
  } = props;

  const [purchaseCount, setPurchaseCount] = useState(0);
  const purchaseInputRef = useRef(null);
  // 单价
  const [price, setPrice] = useState(0);

  const totalPrices = useMemo(() => {
    return Math.round(price * purchaseCount * 100) / 100;
  }, [price, purchaseCount]);

  const luceneBreviaryImageUrlList = useMemo(() => {
    return productVO?.luceneBreviaryImageUrls?.split('<$>') || [];
  }, [productVO]);

  // 动态参数
  const paramLinkedDom = useMemo(() => {
    const max = isMro ? 2 : 4;
    return (
      paramLinkedMap &&
      Object.entries(paramLinkedMap)
        ?.filter(([, value]) => value && value != '-')
        .slice(0, max)
        .map(([key, value]) => {
          let label = key;
          if (key.includes('(')) {
            const endIndex = key.indexOf('(');
            label = key.substring(0, endIndex);
          }
          return <LabelText key={key} label={label} text={value} title={value} bp="参数" showLabelTitle></LabelText>;
        })
    );
  }, [paramLinkedMap, isMro]);

  // 描述
  const productInfoDom = useMemo(() => {
    const paramLen = paramLinkedDom?.length;
    if (remarkPrefixTitle) {
      return (
        <MultiLineText
          label="描述"
          text={remarkPrefixTitle}
          title={remarkPrefixTitle}
          line={paramLen > 3 ? 1 : 2}
        ></MultiLineText>
      );
    }
  }, [remarkPrefixTitle]);

  // 跳转详情链接
  const detailPage = useMemo(() => {
    let url = `${resourceData?.projectConfigVO?.lcscItemUrl}/${productVO.productId}.html`;
    if (isMro) {
      url += `?mro=1`;
    }
    if (track_zone) {
      const splitor = url.includes('?') ? '&' : '?';
      url += `${splitor}fromZone=${Utils.safeEncodeURI(track_zone)}`;
    }
    return url;
  }, [isMro, resourceData, productVO, track_zone]);

  function gotoItem(kind) {
    goItemBuriedPoint({
      kind,
      pc: productVO?.productId,
      brand: productVO?.productGradePlateName,
      model: productVO?.productModel,
      code: productVO?.productCode,
      kw: searchResult?.globalSearchKeyword,
      cp: searchResult?.currePage,
      ix: index,
      track_zone: track_zone,
      type: 'mianProduct',
      rs_log,
    });
  }

  function commonBuried(op) {
    buriedPoint({
      op,
      data: {
        pc: productVO?.productId,
        brand: productVO?.productGradePlateName,
        model: productVO?.productModel,
        code: productVO?.productCode,
        cp: searchResult?.currePage,
        kw: searchResult?.globalSearchKeyword,
        ix: index,
        rs_log,
      },
    });
  }

  return (
    <div
      className={`flex bg-[#F3F9FD] px-[20px] py-[15px] leading-[18px] ${showBorder ? 'border-b border-solid border-[#EFEFEF]' : ''}`}
    >
      <div>
        <div className="flex">
          <div className="relative h-[105px] w-[121px] select-none">
            <a
              aria-label="点击跳转详情"
              href={detailPage}
              data-spm="t"
              target="_blank"
              onClick={() => gotoItem('picture')}
            >
              {showTag && <ActivityTag {...props}></ActivityTag>}
              {sideTitle && (
                <div
                  className={
                    'absolute left-[0px] top-[0px]  z-[10]  flex h-[20px] items-center  rounded-[4px_0_10px_0] bg-[#0093E6] px-[6px] text-center text-[12px] text-white'
                  }
                  title={sideTitle}
                >
                  <div className={'max-w-[80px] truncate'}> {sideTitle}</div>
                </div>
              )}
              <PicturePerview
                src={productVO?.breviaryImageUrl}
                imageList={luceneBreviaryImageUrlList}
                alt={productVO?.productName}
                productId={productVO?.productId}
              />
            </a>
          </div>
          {isMro ? (
            <div className={Utils.addBigScreenClassName('w-[368px]', '_lg:w-[448px]', onlySScreen)}>
              <LabelText
                text={productVO?.productName}
                title={productVO?.productName}
                isTitle
                textBold
                isMro
                link={detailPage}
                spm="n"
                bp="型号"
                onClick={() => gotoItem('name')}
              ></LabelText>
              <div className="flex">
                <div className={Utils.addBigScreenClassName('w-[178px]', '_lg:w-[215px]', onlySScreen)}>
                  <LabelText
                    label="品牌"
                    text={productVO?.productGradePlateName}
                    title={productVO?.productGradePlateName}
                    link={`${resourceData?.projectConfigVO?.lcscListUrl}${isMro ? '/mro' : ''}/brand/${productVO?.productGradePlateId}.html`}
                    underline
                    spm="p"
                    onClick={() => commonBuried('go_brand')}
                  ></LabelText>
                  <LabelText
                    label="类目"
                    text={productVO?.productType}
                    title={productVO?.productType}
                    link={`${resourceData?.projectConfigVO?.lcscListUrl}/catalog/${productVO.productTypeCode}.html${isMro ? '?mro=1' : ''}`}
                    underline
                    spm="l"
                    onClick={() => commonBuried('go_catalog')}
                  ></LabelText>
                  <LabelText label="型号" text={productVO?.productModel} title={productVO?.productModel}></LabelText>
                </div>
                <div className={Utils.addBigScreenClassName('w-[190px]', '_lg:w-[233px]', onlySScreen)}>
                  <LabelText label="编号" text={productVO?.productCode} title={productVO?.productCode}></LabelText>
                  {paramLinkedDom}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className={Utils.addBigScreenClassName('w-[178px]', '_lg:w-[215px]', onlySScreen)}>
                <LabelText
                  text={productVO?.productModel}
                  title={productVO?.productModel}
                  isTitle
                  textBold
                  link={detailPage}
                  spm="n"
                  bp="型号"
                  onClick={() => gotoItem('name')}
                ></LabelText>
                <LabelText
                  label="品牌"
                  text={productVO?.productGradePlateName}
                  title={productVO?.productGradePlateName}
                  link={`${resourceData?.projectConfigVO?.lcscListUrl}${isMro ? '/mro' : ''}/brand/${productVO?.productGradePlateId}.html`}
                  underline
                  spm="p"
                  onClick={() => commonBuried('go_brand')}
                ></LabelText>
                <LabelText
                  label="封装"
                  text={productVO?.encapsulationModel}
                  title={productVO?.encapsulationModel}
                ></LabelText>
                <LabelText
                  label="类目"
                  text={productVO?.productType}
                  title={productVO?.productType}
                  link={`${resourceData?.projectConfigVO?.lcscListUrl}/catalog/${productVO.productTypeCode}.html${isMro ? '?mro=1' : ''}`}
                  underline
                  spm="l"
                  onClick={() => commonBuried('go_catalog')}
                ></LabelText>
                <LabelText label="编号" text={productVO?.productCode} title={productVO?.productCode}></LabelText>
              </div>
              <div className={Utils.addBigScreenClassName('w-[190px]', '_lg:w-[233px]', onlySScreen)}>
                {paramLinkedDom}
                {productInfoDom}
              </div>
            </>
          )}
        </div>
      </div>
      <div className={Utils.addBigScreenClassName('w-[157px]', '_lg:w-[180px]', onlySScreen)}>
        {productVO?.productUnsealVendition !== 'no' && (
          <ProductTags {...props} totalPrices={totalPrices} purchaseCount={purchaseCount}></ProductTags>
        )}
      </div>
      <div className="flex flex-1 flex-col items-center">
        {productVO?.productUnsealVendition !== 'no' && (
          <PriceLevelAndDiscount {...props} purchaseCount={purchaseCount} onChange={(value) => setPrice(value)} />
        )}
      </div>
      <div className={Utils.addBigScreenClassName('w-[161px]', '_lg:w-[186px]', onlySScreen)}>
        {productVO?.productUnsealVendition !== 'no' && <StockAndDeliver {...props} needBold={needBold} />}
      </div>
      <div className={`${Utils.addBigScreenClassName('w-[120px]', '_lg:w-[140px]', onlySScreen)} text-right`}>
        {productVO?.productUnsealVendition !== 'no' && (
          <>
            <PurchaseNumInputs
              ref={purchaseInputRef}
              productVO={productVO}
              totalStockNumber={totalStockNumber}
              onChange={(value) => setPurchaseCount(value)}
            />
            <TotalPrice totalPrices={totalPrices} />
          </>
        )}
        <PurchaseButton
          {...props}
          purchaseCount={purchaseCount}
          totalPrices={totalPrices}
          validate={purchaseInputRef?.current?.validate}
          suggest={suggest}
        />
      </div>
    </div>
  );
}

export default ProductSpuItem;
